import { Injectable, OnInit } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import * as L from "leaflet";
import { Config } from "../../config";
import { Condition } from "../../_models/condition";
import { GridElement } from "../../_models/grid-element";
import { environment } from "../../../environments/environment";
import { GridElementSubType } from "../../_models/grid-element-subtype";
import { Observable, throwError } from "rxjs";
import { CableDetails } from "../../_models/cable-details";
import { htmlAstToRender3Ast } from "@angular/compiler/src/render3/r3_template_transform";
import { ConfigurationService } from "../configuration/configuration.service";
import { TopologyConfig } from "src/app/_models/topology-list";
import { catchError, max } from "rxjs/operators";
import { ModalService } from "../modal/modal.service";
import { TestingDataForPost } from "src/app/_models/userData";
import { element } from 'protractor';

@Injectable({
  providedIn: "root",
})
export class MapService {
  private map: L.map;
  private topology: TopologyConfig;
  private baseLayer?: L.tileLayer;
  private cartesianMapBounds: number;
  private enableZoomButtonsOnTheMap: boolean;

  ENDPOINT_GET_CABLES_AND_COORDINATES: string;
  ENDPOINT_GET_GRID_ELEMENT_DETAILS: string;
  ENDPOINT_GET_CABLE_DETAILS: string;
  ENDPOINT_GET_GPLAN_NODE_IDS: string;
  ENDPOINT_GET_ALL_CABLE_DETAILS: string;
  ENDPOINT_GET_CABLE_TYPES: string;
  ENDPOINT_GET_ROOT_NODE: string;
  ENDPOINT_GET_AVAILABLE_TOPOLOGIES: string;
  ENDPOINT_GET_GRID_ELEMENT_TYPES: string;
  ENDPOINT_GET_GRID_TYPE: string
  ENDPOINT_GET_SWITCHING_STATE: string;
  ENDPOINT_GET_SWITCHING_STATE_TO_NEIGHBOUR: string;


  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private modalService: ModalService
  ) {
    //this.topology = this.configurationService.getConfiguration().TOPOLOGY;

    this.cartesianMapBounds = this.configurationService.getConfiguration().StructuralMap.zoom;
    this.enableZoomButtonsOnTheMap = this.configurationService.getConfiguration().enableZoomButtonsOnTheMap ? this.configurationService.getConfiguration().enableZoomButtonsOnTheMap : false; 

    this.ENDPOINT_GET_GRID_ELEMENT_DETAILS =
      environment.API_BASE_URL + "topology/getElementDetails";
    this.ENDPOINT_GET_CABLE_DETAILS =
      environment.API_BASE_URL + "topology/getCableDetails";
    this.ENDPOINT_GET_GPLAN_NODE_IDS =
      environment.API_BASE_URL + "topology/getEdgeLeaveNodes";
    this.ENDPOINT_GET_ALL_CABLE_DETAILS =
      environment.API_BASE_URL + "topology/getAllCableDetails";
    this.ENDPOINT_GET_CABLE_TYPES =
      environment.API_BASE_URL + "topology/getCableTypes";
    this.ENDPOINT_GET_ROOT_NODE =
      environment.API_BASE_URL + "topology/getRootNodeDsoId";
    this.ENDPOINT_GET_AVAILABLE_TOPOLOGIES =
      environment.API_BASE_URL + "topology/getAvaiableTopologies";
    this.ENDPOINT_GET_GRID_ELEMENT_TYPES =
      environment.API_BASE_URL + "topology/getElementTypes";
    this.ENDPOINT_GET_GRID_TYPE =
      environment.API_BASE_URL + "topology/getGridType";
    this.ENDPOINT_GET_SWITCHING_STATE =
      environment.API_BASE_URL + "topology/getSwitchingStateInTimePeriod";
    this.ENDPOINT_GET_SWITCHING_STATE_TO_NEIGHBOUR =
      environment.API_BASE_URL + "topology/getSwitchingStateToNeighbourTopologyInTimePeriod";
  }

  public getIconUrl(
    gridElementType: string,
    gridNodeStatus: Condition
  ): string {
    let iconUrl = "assets/images/";

    switch (gridElementType) {
      case "Transformer":
        iconUrl += "transformer";
        break;
      case "JunctionBox":
        iconUrl += "junction-box";
        break;
      case "CustomerConnectionBox":
        iconUrl += "customer-connection-box";
        break;
      case "Generator":
        iconUrl += "generator";
        break;
      case "Meter":
        iconUrl += "meter";
        break;
      case "Sleeve":
        iconUrl += "sleeve";
        break;
      case "LvSubstationBusBarOneTrafo":
        iconUrl += "ssb";
        break;
      case "MvSubstationBusBarOneTrafo":
        iconUrl += "ssb";
        break;
      case "LvSubstationBusBarWithoutTrafo":
        iconUrl += "ssb";
        break;
      case "MvSubstationBusBarWithoutTrafo":
        iconUrl += "ssb";
        break;
      case "MvStationOneTrafo":
        iconUrl += "transformer";
        break;
      case "MvStationWithoutTrafo":
        iconUrl += "junction-box";
        break;
      case "MvStationMultipleTrafo":
        iconUrl += "multitransformer";
        break;
      case "LvJunctionBox":
        iconUrl += "junction-box";
        break;
      case "MvJunctionBox":
        iconUrl += "junction-box";
        break;
      case "Statistics":
        iconUrl += "statistics";
        break;
      case "Scenario":
        iconUrl += "scenario";
        break;
      case "OpenSwitch":
        iconUrl += "open-switch";
        break;
      case "Controllable-device":
        iconUrl += "curt_device";
        break;
      case "VoltageRegulator":
        iconUrl += "voltage-regulator"
        break;
      default:
        iconUrl += "question-mark";
        break;
    }

    return iconUrl + "-icon-" + gridNodeStatus + ".svg";
  }

  addBaseMapLayer(mapId: string): void {
    this.baseLayer = L.tileLayer(Config.MAP_URL, {
      maxNativeZoom: 18,
      maxZoom: 25,
      attribution: "© GridData",
    });
    this.baseLayer.addTo(this.map);
  }

  hideBaseLayer(): void{
    if(this.baseLayer && this.map.hasLayer(this.baseLayer)){
      this.map.removeLayer(this.baseLayer);
    }
  }

  showBaseLayer(): void{
    if(this.baseLayer && !this.map.hasLayer(this.baseLayer)){
      this.baseLayer.addTo(this.map);
    }
  }

  buildBaseMap(mapId: string): void {
    this.topology = JSON.parse(localStorage.getItem("currentTopology"));
    this.map = L.map(mapId, {
      center: [this.topology.mapCenterLat, this.topology.mapCenterLon],
      zoom: this.configurationService.getConfiguration().TOPOLOGY.zoomLevel,
      zoomControl: this.enableZoomButtonsOnTheMap
    });

    this.addBaseMapLayer(this.map);

    return this.map;
  }

  buildGridNodeMarker(gridElementTmp: GridElement, showIds: boolean, showSubIds?: boolean): L.marker {
    let marker;
    let gridElement = new GridElement(
      gridElementTmp.gridElementId,
      gridElementTmp.elementSubType,
      gridElementTmp.properties,
      gridElementTmp.condition
    );

    // this.nodesMap.set(gridElement.gridElementId, gridElement);

    let internalId: string = gridElement.getPropertyValue("name");

    let subId: string = gridElement.getPropertyValue("eflValue");

    // console.log("GridElement are here : ", gridElement);
    let locationX: number = parseFloat(
      gridElement.getPropertyValue("location_x")
    );
    let locationY: number = parseFloat(
      gridElement.getPropertyValue("location_y")
    );

    if (locationX && locationY) {
      // console.log(locationX);
      let iconAnchor: number[] = [10, 12];
      if (
        gridElement.elementSubType == GridElementSubType.Transformer ||
        gridElement.elementSubType == GridElementSubType.MvStationOneTrafo ||
        gridElement.elementSubType == GridElementSubType.MvSubstationBusBarOneTrafo
      ) {
        iconAnchor = [15, 25];
      } else if (gridElement.elementSubType == GridElementSubType.Generator) {
        iconAnchor = [11, 34];
      }

      if (!showIds) {
        internalId = "";
      }

      if(showSubIds === false || showSubIds === undefined){
        subId = "";
      }

      // console.log("Here is the subId: ", subId);

      let html: string =
        '<table class="' +
          gridElement.elementSubType.toLowerCase() +
          '">' +
          '<tr>' +
            '<td>' +
              '<img class="grid-node-' +
                gridElement.elementSubType.toLowerCase() +
                  '" src="' +
                    this.getIconUrl(gridElement.elementSubType, gridElement.condition) +
              '" />' +
            '</td>' +
            '<td>' +
              '<div class="marker-text">' +
                '<span class="marker-label node-id-hidden">' +
                  internalId +
                '</span>' +
                '<span class="marker-subtitle node-sub-id-hidden">' +
                  subId +
                '</span>' +
              '</div>' +
            '</td>' +
          '</tr>' +
        '</table>';


        if(gridElement.elementSubType == "VoltageRegulator"){
          console.log("Here is the url of the icon: ", this.getIconUrl(gridElement.elementSubType, gridElement.condition));
        }

      marker = L.marker([locationX, locationY], {
        icon: new L.DivIcon({
          className: "my-div-icon",
          html: html,
          iconAnchor: iconAnchor,
        }),
        clickable: true,
        title: internalId,
        // draggable: true,
        label: {
          message: internalId,
          options: {
            noHide: true,
          },
        },
      });
    }
    else {
      console.log("Coordinates of gridElement " + gridElement.gridElementId + " are missing")
    }

    return marker;
  }

  getGridElementSubId(gridElement: GridElement): string{
    // console.log("Getting subId for gridElement: ", gridElement);
    switch(gridElement.condition){
      case Condition.HIGH_FAULT_PROBABILITY:
        return "20%";
      case Condition.MID_FAULT_PROBABILITY:
        return "15%";
      case Condition.LOW_FAULT_PROBABILITY:
        return "11%";
      default:
        return "";
    }
  }

  buildGridNodeMarkerForLVTopology(gridElementTmp: GridElement, showIds: boolean, condition: Condition): L.marker {
    let marker;

    let gridElement = new GridElement(
      gridElementTmp.gridElementId,
      gridElementTmp.elementSubType,
      gridElementTmp.properties,
      gridElementTmp.condition
    );

    // this.nodesMap.set(gridElement.gridElementId, gridElement);

    let internalId: string = gridElement.getPropertyValue("name");
    let locationX: number = parseFloat(
      gridElement.getPropertyValue("location_x")
    );
    let locationY: number = parseFloat(
      gridElement.getPropertyValue("location_y")
    );

    if (locationX && locationY) {
      // console.log(locationX);
      let iconAnchor: number[] = [10, 12];
      if (
        gridElement.elementSubType == GridElementSubType.Transformer ||
        gridElement.elementSubType == GridElementSubType.MvStationOneTrafo ||
        gridElement.elementSubType == GridElementSubType.MvSubstationBusBarOneTrafo
      ) {
        iconAnchor = [15, 25];
      }
      else if (gridElement.elementSubType == GridElementSubType.Generator) {
        iconAnchor = [11, 34];
      }

      if (!showIds) {
        internalId = "";
      }

      let html: string =
        '<table class="' +
        gridElement.elementSubType.toLowerCase() +
        '">' +
        "<tr>" +
        '<td><img class="grid-node-' +
        gridElement.elementSubType.toLowerCase() +
        '" src="' +
        this.getIconUrl(gridElement.elementSubType, condition) +
        '" /></td>' +
        // '<td><span class="marker-label">' + internalId + ' (' + gridElement.gridElementId  + ')</span></td>' +
        '<td><span class="marker-label node-id-hidden">' +
        internalId +
        "</span></td>" +
        "</td>" +
        "</tr>";

      marker = L.marker([locationX, locationY], {
        icon: new L.DivIcon({
          className: "my-div-icon",
          html: html,
          iconAnchor: iconAnchor,
        }),
        clickable: true,
        title: internalId,
        // draggable: true,
        label: {
          message: internalId,
          options: {
            noHide: true,
          },
        },
      });
    }
    else {
      console.log("Coordinates of gridElement " + gridElement.gridElementId + " are missing")
    }

    return marker;
  }


  buildGridNodeMarkerForLVTopologyTestingOne(gridElementTmp: GridElement, showIds: boolean, condition: Condition): L.marker {
    let marker;

    let gridElement = new GridElement(
      gridElementTmp.gridElementId,
      gridElementTmp.elementSubType,
      gridElementTmp.properties,
      gridElementTmp.condition
    );

    // this.nodesMap.set(gridElement.gridElementId, gridElement);

    let internalId: string = gridElement.getPropertyValue("name");
    let locationX: number = parseFloat(
      gridElement.getPropertyValue("location_x")
    );
    let locationY: number = parseFloat(
      gridElement.getPropertyValue("location_y")
    );

    if (locationX && locationY) {
      // console.log(locationX);
      let iconAnchor: number[] = [210, 222];
      if (
        gridElement.elementSubType == GridElementSubType.Transformer ||
        gridElement.elementSubType == GridElementSubType.MvStationOneTrafo ||
        gridElement.elementSubType == GridElementSubType.MvSubstationBusBarOneTrafo
      ) {
        iconAnchor = [215, 225];
      }
      else if (gridElement.elementSubType == GridElementSubType.Generator) {
        iconAnchor = [211, 234];
      }

      if (!showIds) {
        internalId = "";
      }

      let html: string =
        '<table class="' +
        gridElement.elementSubType.toLowerCase() +
        '">' +
        "<tr>" +
        '<td><img class="grid-node-' +
        gridElement.elementSubType.toLowerCase() +
        '" src="' +
        this.getIconUrl(gridElement.elementSubType, condition) +
        '" /></td>' +
        // '<td><span class="marker-label">' + internalId + ' (' + gridElement.gridElementId  + ')</span></td>' +
        '<td><span class="marker-label node-id-hidden">' +
        internalId +
        "</span></td>" +
        "</td>" +
        "</tr>";

      marker = L.marker([locationX, locationY], {
        icon: new L.DivIcon({
          className: "my-div-icon",
          html: html,
          iconAnchor: iconAnchor,
        }),
        clickable: true,
        title: internalId,
        // draggable: true,
        label: {
          message: internalId,
          options: {
            noHide: true,
          },
        },
      });
    }
    else {
      console.log("Coordinates of gridElement " + gridElement.gridElementId + " are missing")
    }

    return marker;
  }


  buildSwitchMarker(locationX: number, locationY: number): L.marker {
    let marker;
    if (locationX && locationY) {
      // console.log(locationX);
      let iconAnchor: number[] = [10, 12];


      let html: string =
        '<table class="open-switch-cable">' +
        "<tr>" +
        '<td><img class="open-switch-cable" src="' +
        this.getIconUrl(GridElementSubType.OpenSwitch, Condition.NORMAL)  +
        '" /></td>' +


        "</td>" +
        "</tr>";

      marker = L.marker([locationX, locationY], {
        icon: new L.DivIcon({
          className: "my-div-icon",
          html: html,
          iconAnchor: iconAnchor,
        }),
        clickable: true,
        // draggable: true,
        label: {
          options: {
            noHide: true,
          },
        },
      });
    }
    else {
      console.log("Coordinates of the switch are missing")
    }

    return marker;
  }

  buildGridNodeMarker2(gridElementTmp: GridElement, condition: Condition): L.marker {
    let marker;

    let gridElement = new GridElement(
      gridElementTmp.gridElementId,
      GridElementSubType.Statistics,
      gridElementTmp.properties,
      gridElementTmp.condition
    );

    let internalId: string = gridElement.getPropertyValue("name");
    let locationX: number = parseFloat(
      gridElement.getPropertyValue("location_x")
    );
    let locationY: number = parseFloat(
      gridElement.getPropertyValue("location_y")
    );

    if (locationX && locationY) {
      let iconAnchor: number[] = [35, 12];

      let html: string =
        '<table class="' + GridElementSubType.Statistics.toLowerCase() + '">' +
        '<tr>' +

        //pass Condition.NORMAL to getIconURL method to view currently the M symbol always green
        '<td><img class="grid-node-' + GridElementSubType.Statistics.toLowerCase() + '" src="' + this.getIconUrl(GridElementSubType.Statistics, condition) + '" " style="z-index=0;"></td>' +

        //'<td><img class="grid-node-' +  GridElementSubType.Statistics.toLowerCase() + '" src="' + this.getIconUrl(GridElementSubType.Statistics, gridElement.condition) + '" /></td>' +
        "</td>" +
        "</tr>";

      marker = L.marker([locationX, locationY], {
        icon: new L.DivIcon({
          className: "my-div-icon",
          html: html,
          iconAnchor: iconAnchor,
        }),
        clickable: true,
        title: internalId,
        // draggable: true,
        label: {
          message: internalId,
          options: {
            noHide: true,
          },
        },
      });
    }

    return marker;
  }

  buildControllableIcon(gridElementTmp: GridElement, condition: Condition): L.marker {
    let marker;

    let gridElement = new GridElement(
      gridElementTmp.gridElementId,
      GridElementSubType.ControllableDevice,
      gridElementTmp.properties,
      gridElementTmp.condition
    );

    let internalId: string = gridElement.getPropertyValue("name");
    let locationX: number = parseFloat(
      gridElement.getPropertyValue("location_x")
    );
    let locationY: number = parseFloat(
      gridElement.getPropertyValue("location_y")
    );

    if (locationX && locationY) {
      let iconAnchor: number[] = [18, -7];

      let html: string =
        '<table class="' + GridElementSubType.ControllableDevice.toLowerCase() + '">' +
        '<tr>' +

        //pass Condition.NORMAL to getIconURL method to view currently the M symbol always green
        '<td><img class="grid-node-' + GridElementSubType.ControllableDevice.toLowerCase() + '" src="' + this.getIconUrl("Controllable-device", condition) + '" " style="z-index=0;"></td>' +

        //'<td><img class="grid-node-' +  GridElementSubType.Statistics.toLowerCase() + '" src="' + this.getIconUrl(GridElementSubType.Statistics, gridElement.condition) + '" /></td>' +
        "</td>" +
        "</tr>";

        console.log(locationX + " -- " + locationY);
        console.log(iconAnchor)
        console.log(html)

      marker = L.marker([locationX, locationY], {
        icon: new L.DivIcon({
          className: "my-div-icon",
          html: html,
          iconAnchor: iconAnchor,
        }),
        clickable: true,
        title: internalId,
        // draggable: true,
        label: {
          message: internalId,
          options: {
            noHide: true,
          },
        },
      });
    }

    return marker;
  }


  /**
   * Build grid node markers for "Loads" of the selected scenario
   * Use a black generator icon to show in the map
   * @param gridElementTmp
   * @returns
   */
  buildGridNodeMarkerScenario(gridElementTmp: GridElement): L.marker {
    let marker;

    let gridElement = new GridElement(
      gridElementTmp.gridElementId,
      GridElementSubType.Scenario,
      gridElementTmp.properties,
      gridElementTmp.condition
    );

    let internalId: string = gridElement.getPropertyValue("name");
    let locationX: number = parseFloat(
      gridElement.getPropertyValue("location_x")
    );
    let locationY: number = parseFloat(
      gridElement.getPropertyValue("location_y")
    );

    if (locationX && locationY) {
      let iconAnchor: number[] = [-2, -12];

      let html: string =
        '<table class="' + GridElementSubType.Scenario.toLowerCase() + '">' +
        "<tr>" +
        '<td><img class="grid-node-' + GridElementSubType.Scenario.toLowerCase() + '" src="' + this.getIconUrl(GridElementSubType.Scenario, Condition.NORMAL) + '" /></td>' +
        "</td>" +
        '<td><span class="marker-label">' +
        internalId +
        "</span></td>"
        "</tr>";

      marker = L.marker([locationX, locationY], {
        icon: new L.DivIcon({
          className: "my-div-icon",
          html: html,
          iconAnchor: iconAnchor,
        }),
        clickable: true,
        title: internalId,
        //draggable: true,
        label: {
          message: internalId,
          options: {
            noHide: true,
          },
        },
      });
    }
    return marker;
  }

  emptyMap(mapId: string) {
    this.map.eachLayer((layer) => {
      this.map.removeLayer(layer);
    });
    this.addBaseMapLayer(this.map);
  }

  destroyMap(): void {
    if (this.map) {
      this.map.off();
      this.map.remove();
      this.map = null;
    }
  }

  initializeCartesianMap(): L.Map {
    this.map = L.map('map', {
      crs: L.CRS.Simple
    });

    const bounds: L.LatLngBoundsExpression = [[0, 0], [this.cartesianMapBounds, this.cartesianMapBounds]];
    this.map.fitBounds(bounds);

    this.drawGrid(bounds);

    return this.map;
  }


  drawGrid(bounds: L.LatLngBoundsExpression): void {

    const max = this.cartesianMapBounds;

    for (let i = 0; i <= max; i += 1) {

      // horizontal grid line
      L.polyline([[i, 0], [i, max]], {
        color: 'gray',
        weight: 0.5
      }).addTo(this.map);

      // vertical grid line
      L.polyline([[0, i], [max, i]], {
        color: 'gray',
        weight: 0.5
      }).addTo(this.map);
    }
  }

  drawElements(data: any[]): void {

    const groups = {};

    data.forEach(el => {

      const latlng: [number, number] = [el.y, el.x];

      // some rectangle to illustrate the element, in real case it can be marker with icon
      L.rectangle([
        [el.y, el.x],
        [el.y + 1, el.x + 1]
      ], {
        color: 'green'
      })
      .addTo(this.map)

      L.marker([el.y, el.x], {
        icon: L.divIcon({
          className: 'label',
          html: `<span style="
            font-size:10px;
            background:white;
            padding:2px;
            border:1px solid black;
          ">${el.x},${el.y}</span>`
        })
      }).addTo(this.map);

      // rectangles are connected by their gridelementid
      if (!groups[el.gridelementid]) {
        groups[el.gridelementid] = [];
      }

      groups[el.gridelementid].push(latlng);
    });

    // draw lines between elements of the same group
    Object.values(groups).forEach((points: any) => {
      L.polyline(points, { color: 'blue', weight: 1 }).addTo(this.map);
    });
  }

  buildCartesianMarker(
    gridElement: GridElement,
    data: TestingDataForPost,
    showIds: boolean,
    showSubIds?: boolean): L.Marker
  {

    let marker;
    // let locationX: number = data.x;
    // let locationY: number = data.y;
    // console.log("GRID ELEMENT TO DRAW ", gridElement);

    let gridElementToDraw = new GridElement(
      gridElement.gridElementId,
      gridElement.elementSubType,
      gridElement.properties,
      gridElement.condition
    )

    let internalId: string = gridElementToDraw.getPropertyValue("name");
    let subId: string = gridElementToDraw.getPropertyValue("eflValue");

    console.log("@@@@"+subId);
    let locationX: number = data.x;
    let locationY: number = data.y;

    if(locationX && locationY){
      let iconAnchor: number[] = [12, 12];
      if(
        gridElementToDraw.elementSubType == GridElementSubType.Transformer ||
        gridElementToDraw.elementSubType == GridElementSubType.MvStationOneTrafo ||
        gridElementToDraw.elementSubType == GridElementSubType.MvSubstationBusBarOneTrafo
      ){
        iconAnchor = [15, 25];
      } else if (gridElementToDraw.elementSubType == GridElementSubType.Generator) {
        iconAnchor = [11, 34];
      }

      if(gridElement.condition === undefined || gridElement.condition === null){
        gridElement.condition = Condition.NORMAL;
      }


      if (!showIds) {
        internalId = "";
      }

      if(showSubIds === false || showSubIds === undefined){
        subId = "";
      }

      let html: string =
        '<table class="' +
          gridElement.elementSubType.toLowerCase() +
          '">' +
          '<tr>' +
            '<td>' +
              '<img class="grid-node-' +
                gridElement.elementSubType.toLowerCase() +
                  '" src="' +
                    this.getIconUrl(gridElement.elementSubType, gridElement.condition) +
              '" />' +
            '</td>' +
            '<td>' +
              '<div class="marker-text">' +
                '<span class="marker-label node-id-hidden">' +
                  internalId +
                '</span>' +
                '<span class="marker-subtitle node-sub-id-hidden">' +
                  subId +
                '</span>' +
              '</div>' +
            '</td>' +
          '</tr>' +
        '</table>';

        marker = L.marker([locationY, locationX], {
          icon: new L.DivIcon({
            className: "my-div-icon",
            html: html,
            iconAnchor: iconAnchor,
          }),
          clickable: true,
          title: internalId,
          // draggable: true,
          label: {
            message: internalId,
            options: {
              noHide: true,
            },
          },
        });
      }
      else {
        console.log("Coordinates of gridElement " + gridElement.gridElementId + " are missing")
      }

      return marker;
  }

  public getGridElementsDetails() {
    return this.http.get(environment.API_BASE_URL + "topology/getGuiElements/" + this.topology.topologyId);
  }

  public getGridElementsDetailsForCartesianMap(selectedTopologyId: string){
    return this.http.get(environment.API_BASE_URL + "topology/getGuiElements/" + selectedTopologyId);
  }
  /**
   * This method retuns gridElements for the topology @param topologyId
   * @param topologyId
   * @returns gui elements to the related topoloyg
   */
  public getGridElementDetailsFromMVTopology(topologyId: string){
    return this.http.get(environment.API_BASE_URL + "topology/getGuiElements/" + topologyId).pipe(catchError((error: HttpErrorResponse) => {
      this.modalService.openBasicAlert("FAILUER TITLE ", "FAILURE TEXT");
      return throwError(error)
    }));
  }

  public getSubGridElementDetails(){
    return this.http.get(environment.API_BASE_URL + "topology/getGuiElements/ArtGridLV")                                                // HARDCODED need to change!
  }

  //time dependant topology rest API
  public getGridElementsDetailsTimeDependant(date: string) {
    return this.http.get(environment.API_BASE_URL + "topologyTimeDependent/getGuiElements/" + this.topology.topologyId + "/" + date);
  }

  public getCablesAndCoordinates() {
    return this.http.get(environment.API_BASE_URL + "topology/getCablesAndCoordinates/" + this.topology.topologyId);
  }

  public getCablesAndCoorditanesFromMvTopology(topologyId: string){
    return this.http.get(environment.API_BASE_URL + "topology/getCablesAndCoordinates/" + topologyId)
  }

  //time dependant topology rest API
  public getCablesAndCoordinatesTimeDependant(date: string) {
    return this.http.get(environment.API_BASE_URL + "topologyTimeDependent/getCablesAndCoordinates/" + this.topology.topologyId + "/" + date);
  }


  public getGridElementDetails(gridElementId: number) {
    return this.http.get(
      this.ENDPOINT_GET_GRID_ELEMENT_DETAILS + "/" + gridElementId
    );
  }

  public getCableDetails(gridElementId: number): Observable<CableDetails> {
    return this.http.get<CableDetails>(
      this.ENDPOINT_GET_CABLE_DETAILS + "/" + gridElementId
    );
  }

  public isTrafo(gridElementSubType: string): boolean {
    switch (gridElementSubType) {
      case "Transformer":
        return true;
      case "JunctionBox":
        return false;
      case "CustomerConnectionBox":
        return false;
      case "Generator":
        return false;
      case "Meter":
        return false;
      case "Sleeve":
        return false;
      case "LvSubstationBusBarOneTrafo":
        return true;
      case "MvSubstationBusBarOneTrafo":
        return true;
      case "LvSubstationBusBarWithoutTrafo":
        return false;
      case "MvSubstationBusBarWithoutTrafo":
        return false;
      case "MvStationOneTrafo":
        return true;
      case "MvStationWithoutTrafo":
        return false;
      case "MvStationMultipleTrafo":
        return true;
      case "LvJunctionBox":
        return false;
      case "MvJunctionBox":
        return false;
      default:
        return false;
    }
  }

  public getGPlanNodeDetails(topologyId: string): Observable<any> {
    console.log("GETTING GPLAN NODES ", topologyId)
    return this.http.get(this.ENDPOINT_GET_GPLAN_NODE_IDS + "/" + topologyId);
  }

  //time dependant topology rest API
  public getGPlanNodeDetailsTimeDependant(topologyId: string, date: string): Observable<any> {
    return this.http.get(environment.API_BASE_URL + "topologyTimeDependent/getEdgeLeaveNodes" + "/" + topologyId + "/" + date);
  }


  public getAllCableDetails(topologyId: string): Observable<any> {
    return this.http.get(
      this.ENDPOINT_GET_ALL_CABLE_DETAILS + "/" + topologyId
    );
  }

  public getCableDetailsFromLVTopology(): Observable<any>{
    return this.http.get(this.ENDPOINT_GET_ALL_CABLE_DETAILS + "/ArtGridLV")        //URL is not correct
  }

  public getCableTypes(): Observable<any> {
    return this.http.get(this.ENDPOINT_GET_CABLE_TYPES);
  }

  public getRootNode(topologyId: string): Observable<any> {
    return this.http.get(this.ENDPOINT_GET_ROOT_NODE + "/" + topologyId);
  }

  //time dependant topology rest API
  public getRootNodeTimeDependant(topologyId: string, date: string): Observable<any> {
    return this.http.get(environment.API_BASE_URL + "topologyTimeDependent/getRootNodeDsoId" + "/" + topologyId + "/" + date);
  }

  public getAvailableTopologies(): Observable<any> {
    return this.http.get(this.ENDPOINT_GET_AVAILABLE_TOPOLOGIES);
  }

  public getElementTypesByTopologyId(topologyId: string): Observable<any> {
    return this.http.get(this.ENDPOINT_GET_GRID_ELEMENT_TYPES + "/" + topologyId);
  }

  //time dependant topology rest API
  public getElementTypesByTopologyIdTimeDependant(topologyId: string, date: string): Observable<any> {
    return this.http.get(environment.API_BASE_URL + "topologyTimeDependent/getElementTypes" + "/" + topologyId + "/" + date);
  }

  public getGridType(topologyId: string): Observable<any> {
    return this.http.get(this.ENDPOINT_GET_GRID_TYPE + "/" + topologyId);
  }

  public getSwitchingStateInTimePeriod(topologyId: string, from: string, to: string): Observable<any> {
    return this.http.get(this.ENDPOINT_GET_SWITCHING_STATE + "/" + topologyId + "/" + from + "/" + to);
  }

  public getSwitchingStateToNeighbourInTimePeriod(topologyId: string, from: string, to: string): Observable<any> {
    return this.http.get(this.ENDPOINT_GET_SWITCHING_STATE_TO_NEIGHBOUR + "/" + topologyId + "/" + from + "/" + to);
  }

  public getLvGridElementsForMvTopology(gridElementId: number){
    return this.http.get(environment.API_BASE_URL + "topology/getLVTopologyIdForMVGridElement/" + parseInt(gridElementId.toString()));
  }

  public postCoordinatesForGridElement(testData: Array<TestingDataForPost>){
    const payload = testData;
    return this.http.post(environment.API_BASE_URL + "changeTopology/updateGridElementCoordinates", payload);
  }

  public getMarkerCoordinatesForCartesianMap(topologyId: string){
    return this.http.get(environment.API_BASE_URL + "topology/getElementXYCoordinates/" + topologyId);
  }

  public getCablesAndCoordinatesForCartesianMap(selectedTopologyId: string) {
    return this.http.get(environment.API_BASE_URL + "topology/getCablesAndCoordinates/" + selectedTopologyId);
  }
}
